/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import {
	ToolsPanel,
	ToolsPanelItem,
	useCustomUnits,
	UnitControl,
} from '@safe-wordpress/components';
import { useState, Platform } from '@safe-wordpress/element';

/**
 * Internal dependencies
 */
import type { ToolsPanelProps } from './types';
import {
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';
import {
	OverflowControl,
	BoxControl,
	SpacingSizesControl,
	getEditorSettings,
	getCustomValueFromPreset,
	getPresetValueFromCustomValue,
	useSpacingSizes,
	type BoxControlValue,
	type FullEditorSettings,
} from './internal';

const AXIAL_SIDES = [ 'horizontal', 'vertical' ];

export function useHasDimensionsPanel( settings: FullEditorSettings ): boolean {
	const hasPadding = useHasPadding( settings );
	const hasMargin = useHasMargin( settings );
	const hasWidth = useHasWidth( settings );
	const hasHeight = useHasHeight( settings );
	const hasOverflow = useHasOverflow( settings );
	const hasMinWidth = useHasMinWidth( settings );
	const hasMaxWidth = useHasMaxWidth( settings );
	const hasMinHeight = useHasMinHeight( settings );
	const hasMaxHeight = useHasMaxHeight( settings );

	return (
		Platform.OS === 'web' &&
		( hasPadding ||
			hasMargin ||
			hasWidth ||
			hasHeight ||
			hasOverflow ||
			hasMinWidth ||
			hasMaxWidth ||
			hasMinHeight ||
			hasMaxHeight )
	);
}

function useHasPadding( settings: FullEditorSettings ): boolean {
	return !! settings?.spacing?.padding;
}

function useHasMargin( settings: FullEditorSettings ): boolean {
	return !! settings?.spacing?.margin;
}

function useHasWidth( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.width;
}

function useHasHeight( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.height;
}

function useHasOverflow( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.overflow;
}

function useHasMinWidth( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.minWidth;
}

function useHasMaxWidth( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.maxWidth;
}

function useHasMinHeight( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.minHeight;
}

function useHasMaxHeight( settings: FullEditorSettings ): boolean {
	return settings?.dimensions?.maxHeight;
}

function useHasSpacingPresets( settings: FullEditorSettings ): boolean {
	const { defaultSpacingSizes, spacingSizes } = settings?.spacing || {};
	return (
		( defaultSpacingSizes !== false &&
			spacingSizes?.default?.length > 0 ) ||
		spacingSizes?.theme?.length > 0 ||
		spacingSizes?.custom?.length > 0
	);
}

export function DimensionsPanel( {
	value,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	const settings = getEditorSettings();
	const showSpacingPresetsControl = useHasSpacingPresets( settings );
	const units = useCustomUnits( {
		availableUnits: [ '%', 'px', 'em', 'rem', 'vw' ],
	} );

	// Minimum Margin Value
	const minimumMargin = -Infinity;
	const [ minMarginValue, setMinMarginValue ] = useState( minimumMargin );

	// Padding
	const showPaddingControl = useHasPadding( settings );
	const paddingValues = useBoxWithPresets( 'padding', value );
	const disabledPaddingSides = useDisabledSides( 'padding', value );
	const paddingSides = Array.isArray( settings?.spacing?.padding )
		? settings?.spacing?.padding
		: settings?.spacing?.padding?.sides;
	const isAxialPadding =
		paddingSides &&
		paddingSides.some( ( side ) => AXIAL_SIDES.includes( side ) );
	const setPaddingValues = useOnChangeTransformer( onChange, 'padding' );
	const hasPaddingValue = () =>
		hasUserValue( value.paddingTop ) ||
		hasUserValue( value.paddingRight ) ||
		hasUserValue( value.paddingBottom ) ||
		hasUserValue( value.paddingLeft );
	const resetPaddingValue = setPaddingValues;
	const isPaddingShownByDefault =
		hasInheritedValue( value.paddingTop ) ||
		hasInheritedValue( value.paddingRight ) ||
		hasInheritedValue( value.paddingBottom ) ||
		hasInheritedValue( value.paddingLeft );

	// Margin
	const showMarginControl = useHasMargin( settings );
	const marginValues = useBoxWithPresets( 'margin', value );
	const disabledMarginSides = useDisabledSides( 'padding', value );
	const marginSides = Array.isArray( settings?.spacing?.margin )
		? settings?.spacing?.margin
		: settings?.spacing?.margin?.sides;
	const isAxialMargin =
		marginSides &&
		marginSides.some( ( side ) => AXIAL_SIDES.includes( side ) );
	const setMarginValues = useOnChangeTransformer( onChange, 'margin' );
	const hasMarginValue = () =>
		hasUserValue( value.marginTop ) ||
		hasUserValue( value.marginRight ) ||
		hasUserValue( value.marginBottom ) ||
		hasUserValue( value.marginLeft );
	const resetMarginValue = setMarginValues;
	const isMarginShownByDefault =
		hasInheritedValue( value.marginTop ) ||
		hasInheritedValue( value.marginRight ) ||
		hasInheritedValue( value.marginBottom ) ||
		hasInheritedValue( value.marginLeft );

	// Width
	const showWidthControl = useHasWidth( settings );

	// Height
	const showHeightControl = useHasHeight( settings );

	// Overflow
	const showOverflowControl = useHasOverflow( settings );
	const overflowValues = {
		horizontal: getPreviewValue( value.overflowX ),
		vertical: getPreviewValue( value.overflowY ),
	};
	const disabledOverflowSides = {
		horizontal: 'readonly' === value.overflowX?.type,
		vertical: 'readonly' === value.overflowY?.type,
	};
	const setOverflowValues = ( {
		horizontal,
		vertical,
	}: { horizontal?: string; vertical?: string } = {} ) =>
		onChange( { overflowX: horizontal, overflowY: vertical } );
	const hasOverflowValue = () =>
		hasUserValue( value.overflowX ) || hasUserValue( value.overflowY );
	const resetOverflowValue = setOverflowValues;
	const isOverflowShownByDefault =
		hasInheritedValue( value.overflowX ) ||
		hasInheritedValue( value.overflowY );

	// Min Width
	const showMinWidthControl = useHasMinWidth( settings );

	// Max Width
	const showMaxWidthControl = useHasMaxWidth( settings );

	// Min Height
	const showMinHeightControl = useHasMinHeight( settings );

	// Max Height
	const showMaxHeightControl = useHasMaxHeight( settings );

	const resetAll = () =>
		onChange( {
			margin: undefined,
			padding: undefined,
			minWidth: undefined,
			width: undefined,
			maxWidth: undefined,
			minHeight: undefined,
			height: undefined,
			maxHeight: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Dimensions', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			{ showPaddingControl && (
				<ToolsPanelItem
					hasValue={ hasPaddingValue }
					label={ _x( 'Padding', 'text', 'nelio-ab-testing' ) }
					onDeselect={ resetPaddingValue }
					isShownByDefault={ isPaddingShownByDefault }
					className={ clsx( {
						'tools-panel-item-spacing': showSpacingPresetsControl,
					} ) }
				>
					{ ! showSpacingPresetsControl ? (
						<BoxControl
							__next40pxDefaultSize
							values={ paddingValues }
							onChange={ setPaddingValues }
							label={ _x(
								'Padding',
								'text',
								'nelio-ab-testing'
							) }
							sides={
								paddingSides as
									| (
											| 'horizontal'
											| 'vertical'
											| keyof BoxControlValue
									  )[]
									| undefined
							}
							units={ units }
							allowReset={ false }
							splitOnAxis={ isAxialPadding }
							disabledSides={ disabledPaddingSides }
						/>
					) : (
						<SpacingSizesControl
							values={ paddingValues }
							disabledSides={ disabledPaddingSides }
							onChange={ setPaddingValues }
							label={ _x(
								'Padding',
								'text',
								'nelio-ab-testing'
							) }
							sides={ paddingSides }
						/>
					) }
				</ToolsPanelItem>
			) }
			{ showMarginControl && (
				<ToolsPanelItem
					hasValue={ hasMarginValue }
					label={ _x( 'Margin', 'text', 'nelio-ab-testing' ) }
					onDeselect={ resetMarginValue }
					isShownByDefault={ isMarginShownByDefault }
					className={ clsx( {
						'tools-panel-item-spacing': showSpacingPresetsControl,
					} ) }
				>
					{ ! showSpacingPresetsControl ? (
						<BoxControl
							__next40pxDefaultSize
							values={ marginValues }
							onChange={ setMarginValues }
							inputProps={ {
								min: minMarginValue,
								onDragStart: () => {
									// Reset to 0 in case the value was negative.
									setMinMarginValue( 0 );
								},
								onDragEnd: () => {
									setMinMarginValue( minimumMargin );
								},
							} }
							label={ _x( 'Margin', 'text', 'nelio-ab-testing' ) }
							sides={
								marginSides as
									| (
											| 'horizontal'
											| 'vertical'
											| keyof BoxControlValue
									  )[]
									| undefined
							}
							units={ units }
							allowReset={ false }
							splitOnAxis={ isAxialMargin }
							disabledSides={ disabledMarginSides }
						/>
					) : (
						<SpacingSizesControl
							values={ marginValues }
							disabledSides={ disabledMarginSides }
							onChange={ setMarginValues }
							minimumCustomValue={ -Infinity }
							label={ _x( 'Margin', 'text', 'nelio-ab-testing' ) }
							sides={ marginSides }
						/>
					) }
				</ToolsPanelItem>
			) }
			{ showWidthControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.width ) }
					label={ _x( 'Width', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { width: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.width ) }
				>
					<UnitControl
						label={ _x( 'Width', 'text', 'nelio-ab-testing' ) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.width ) }
						onChange={ ( width ) => onChange( { width } ) }
						disabled={ 'readonly' === value.width?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ showHeightControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.height ) }
					label={ _x( 'Height', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { height: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.height ) }
				>
					<UnitControl
						label={ _x( 'Height', 'text', 'nelio-ab-testing' ) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.height ) }
						onChange={ ( height ) => onChange( { height } ) }
						disabled={ 'readonly' === value.height?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ showOverflowControl && (
				<ToolsPanelItem
					hasValue={ hasOverflowValue }
					label={ _x( 'Overflow', 'text', 'nelio-ab-testing' ) }
					onDeselect={ resetOverflowValue }
					isShownByDefault={ isOverflowShownByDefault }
				>
					<OverflowControl
						values={ overflowValues }
						onChange={ setOverflowValues }
						disabledSides={ disabledOverflowSides }
						allowReset={ false }
						label={ _x( 'Overflow', 'text', 'nelio-ab-testing' ) }
					/>
				</ToolsPanelItem>
			) }
			{ showMinWidthControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.minWidth ) }
					label={ _x( 'Minimum width', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { minWidth: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.minWidth ) }
				>
					<UnitControl
						label={ _x(
							'Minimum width',
							'text',
							'nelio-ab-testing'
						) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.minWidth ) }
						onChange={ ( minWidth ) => onChange( { minWidth } ) }
						disabled={ 'readonly' === value.minWidth?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ showMaxWidthControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.maxWidth ) }
					label={ _x( 'Maximum width', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { maxWidth: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.maxWidth ) }
				>
					<UnitControl
						label={ _x(
							'Maximum width',
							'text',
							'nelio-ab-testing'
						) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.maxWidth ) }
						onChange={ ( maxWidth ) => onChange( { maxWidth } ) }
						disabled={ 'readonly' === value.maxWidth?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ showMinHeightControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.minHeight ) }
					label={ _x( 'Minimum height', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { minHeight: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.minHeight ) }
				>
					<UnitControl
						label={ _x(
							'Minimum height',
							'text',
							'nelio-ab-testing'
						) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.minHeight ) }
						onChange={ ( minHeight ) => onChange( { minHeight } ) }
						disabled={ 'readonly' === value.minHeight?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ showMaxHeightControl && (
				<ToolsPanelItem
					hasValue={ () => hasUserValue( value.maxHeight ) }
					label={ _x( 'Maximum height', 'text', 'nelio-ab-testing' ) }
					onDeselect={ () => onChange( { maxHeight: undefined } ) }
					isShownByDefault={ !! hasInheritedValue( value.maxHeight ) }
				>
					<UnitControl
						label={ _x(
							'Maximum height',
							'text',
							'nelio-ab-testing'
						) }
						placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
						labelPosition="top"
						units={ units }
						min={ 0 }
						value={ getPreviewValue( value.maxHeight ) }
						onChange={ ( maxHeight ) => onChange( { maxHeight } ) }
						disabled={ 'readonly' === value.maxHeight?.type }
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
		</ToolsPanel>
	);
}

// =======
// HELPERS
// =======

function useBoxWithPresets(
	what: 'margin' | 'padding',
	value: ToolsPanelProps[ 'value' ]
): {
	readonly top?: string;
	readonly right?: string;
	readonly bottom?: string;
	readonly left?: string;
} {
	const spacingSizes = useSpacingSizes();

	const top = getPreviewValue( value[ `${ what }Top` ] );
	const right = getPreviewValue( value[ `${ what }Right` ] );
	const bottom = getPreviewValue( value[ `${ what }Bottom` ] );
	const left = getPreviewValue( value[ `${ what }Left` ] );

	return {
		top:
			undefined !== top
				? getPresetValueFromCustomValue( top, spacingSizes )
				: undefined,
		right:
			undefined !== right
				? getPresetValueFromCustomValue( right, spacingSizes )
				: undefined,
		bottom:
			undefined !== bottom
				? getPresetValueFromCustomValue( bottom, spacingSizes )
				: undefined,
		left:
			undefined !== left
				? getPresetValueFromCustomValue( left, spacingSizes )
				: undefined,
	};
}

function useDisabledSides(
	what: 'margin' | 'padding',
	value: ToolsPanelProps[ 'value' ]
) {
	return {
		top: 'readonly' === value[ `${ what }Top` ]?.type,
		right: 'readonly' === value[ `${ what }Right` ]?.type,
		bottom: 'readonly' === value[ `${ what }Bottom` ]?.type,
		left: 'readonly' === value[ `${ what }Left` ]?.type,
	};
}

function useOnChangeTransformer(
	onChange: ToolsPanelProps[ 'onChange' ],
	what: 'margin' | 'padding'
) {
	const spacingSizes = useSpacingSizes();
	return ( {
		top,
		right,
		bottom,
		left,
	}: {
		readonly top?: string;
		readonly right?: string;
		readonly bottom?: string;
		readonly left?: string;
	} = {} ) => {
		onChange( {
			[ `${ what }Top` ]:
				undefined !== top
					? getCustomValueFromPreset( top, spacingSizes )
					: undefined,
			[ `${ what }Right` ]:
				undefined !== right
					? getCustomValueFromPreset( right, spacingSizes )
					: undefined,
			[ `${ what }Bottom` ]:
				undefined !== bottom
					? getCustomValueFromPreset( bottom, spacingSizes )
					: undefined,
			[ `${ what }Left` ]:
				undefined !== left
					? getCustomValueFromPreset( left, spacingSizes )
					: undefined,
		} );
	};
}
